/**
 * MatterSessionSwitcher - which of this matter's research sessions the
 * Assistant pane is showing.
 *
 * The matter's sessions come from `GET /matters/{uuid}/connected`, which the
 * matter page already fetches for the rail badge (`useConnectedWork`) - so
 * this is a renderer, not a second fetch. The rows are the platform `ListRow`
 * rather than a fourth hand-rolled list anatomy.
 *
 * Deliberately NOT `ResearchHistorySidebar`: that lists the user's WHOLE
 * research history out of the Redux store and pages it on scroll. Here the
 * list is short, matter-scoped, and already in memory, and the pane has no
 * room for a sidebar - so it is a header menu over the same idea.
 *
 * "New session" does not create anything. It returns the pane to the idle
 * state, where the next message creates the matter-linked chat; see
 * `MatterAssistantPane`.
 */

import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCaretDownLight,
  PiChatsCircleLight,
  PiNotePencilLight,
} from 'react-icons/pi';
import ListRow from '../../shared/surface/ListRow';
import { formatDateTime } from '../../../utils/functions';
import { ConnectedResearchSession } from './useConnectedWork';

/**
 * The chat group is what the research endpoints address.
 *
 * `uuid` is the fallback only because the two coincide for sessions created
 * before the session/chat-group split - the same rule `MatterConnectedPane`
 * follows when it links out.
 */
export function sessionChatUuid(session: ConnectedResearchSession): string {
  return session.chat_group_uuid || session.uuid || '';
}

/** Newest first, by whichever timestamp the row carries. */
export function orderSessions(
  sessions: ConnectedResearchSession[]
): ConnectedResearchSession[] {
  const at = (s: ConnectedResearchSession) =>
    Date.parse(s.updated_at || s.last_message_at || '') || 0;
  return [...sessions].sort((a, b) => at(b) - at(a));
}

interface Props {
  sessions: ConnectedResearchSession[];
  loading?: boolean;
  /** The chat currently open, or '' while the pane is idle. */
  activeChatUuid: string;
  /** Title of the open chat - the session hook's, which is authoritative. */
  activeTitle?: string;
  onSelect: (chatUuid: string) => void;
  onNewSession: () => void;
}

const MatterSessionSwitcher: React.FC<Props> = ({
  sessions,
  loading = false,
  activeChatUuid,
  activeTitle,
  onSelect,
  onNewSession,
}) => {
  const { t } = useTranslation();
  const ordered = orderSessions(sessions);

  /**
   * What the trigger says.
   *
   * The open chat's own title wins - it is live, and the connected list is a
   * snapshot that will not carry a chat created a moment ago. The list row is
   * the fallback, and "New session" is what idle looks like.
   */
  const fromList = ordered.find((s) => sessionChatUuid(s) === activeChatUuid);
  const label = activeChatUuid
    ? activeTitle || fromList?.title || t('Untitled session')
    : t('New session');

  return (
    <Menu as="div" className="relative min-w-0">
      <Menu.Button
        data-testid="matter-session-switcher"
        aria-label={t('Switch research session')}
        className="group inline-flex items-center gap-1 max-w-[13rem] min-w-0 px-2 py-1
                   rounded-md text-[11px] font-medium text-fg-body
                   border border-hairline-soft bg-surface-sunken/70
                   hover:border-champagne/50 transition-colors duration-200 ease-slide"
      >
        <PiChatsCircleLight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        <span className="truncate">{label}</span>
        <PiCaretDownLight className="w-3 h-3 flex-shrink-0 text-fg-muted" aria-hidden />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-pop duration-150"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items
          data-testid="matter-session-list"
          className="absolute start-0 z-40 mt-1 w-[17rem] max-w-[80vw] max-h-80 overflow-y-auto
                     glass rounded-lg shadow-sm py-1 focus:outline-none"
        >
          <Menu.Item>
            {({ active }: { active: boolean }) => (
              <button
                type="button"
                onClick={onNewSession}
                data-testid="matter-session-new"
                className={clsx(
                  'flex items-center gap-2 w-full px-3 py-2 text-xs text-start',
                  'border-b border-hairline-soft',
                  active ? 'bg-surface-sunken text-fg-strong' : 'text-fg-body'
                )}
              >
                <PiNotePencilLight className="w-3.5 h-3.5" aria-hidden />
                {t('New session')}
              </button>
            )}
          </Menu.Item>

          {loading ? (
            <p className="px-3 py-3 text-[11px] text-fg-muted">{t('Loading…')}</p>
          ) : ordered.length === 0 ? (
            <p
              className="px-3 py-3 text-[11px] text-fg-muted"
              data-testid="matter-session-empty"
            >
              {t('No research sessions are linked to this matter yet.')}
            </p>
          ) : (
            ordered.map((session, index) => {
              const uuid = sessionChatUuid(session);
              const when = session.updated_at || session.last_message_at;
              return (
                <Menu.Item key={session.uuid || uuid}>
                  {/*
                    Wrapped in a plain element on purpose. Menu.Item clones its
                    child to attach a ref and its keyboard handlers, and
                    `ListRow` is a function component with no forwarded ref —
                    handing it straight to Menu.Item warns and loses focus
                    management. The div takes both; the click bubbles up from
                    the row's own button, which is what closes the menu.
                  */}
                  <div>
                    <ListRow
                      data-testid="matter-session-row"
                      index={index}
                      glyph={
                        <PiChatsCircleLight
                          className="w-4 h-4 text-fg-muted"
                          aria-hidden
                        />
                      }
                      title={session.title || t('Untitled session')}
                      meta={when ? formatDateTime(when) : undefined}
                      rightWidth="w-0"
                      selected={!!uuid && uuid === activeChatUuid}
                      onClick={uuid ? () => onSelect(uuid) : undefined}
                    />
                  </div>
                </Menu.Item>
              );
            })
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default MatterSessionSwitcher;
